import { useState, useEffect, useCallback } from 'react';
import apiClient from '@/lib/api/client';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  getRevenueReport,
  getArAgingReport,
  getProfitabilityReport,
  getCrewUtilizationReport,
  getWinLossReport,
  getGstReport,
  type RevenueReport,
  type ArAgingReport,
  type ProfitabilityRow,
  type UtilizationRow,
  type WinLossReport,
  type GstReport,
} from '@/lib/api/reports';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import { LargeTitleHeader, SegmentedControl } from '@/components/common';

const money = (n: number) =>
  `$${(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

type Tab = 'revenue' | 'ar-aging' | 'profitability' | 'utilization' | 'win-loss' | 'gst';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'revenue', label: 'Revenue', icon: 'paid' },
  { id: 'ar-aging', label: 'AR Aging', icon: 'account_balance_wallet' },
  { id: 'profitability', label: 'Profitability', icon: 'trending_up' },
  { id: 'utilization', label: 'Crew Utilization', icon: 'engineering' },
  { id: 'win-loss', label: 'Win / Loss', icon: 'emoji_events' },
  { id: 'gst', label: 'GST', icon: 'receipt_long' },
];

const AGING_BUCKET_LABELS: Record<string, string> = {
  current: 'Current',
  '1-30': '1–30 days',
  '31-60': '31–60 days',
  '61-90': '61–90 days',
  '90+': '90+ days',
};

const AGING_BUCKET_KEYS = ['current', '1-30', '31-60', '61-90', '90+'];

// ── Revenue Tab ──────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LS_REVENUE_YEAR = 'reports_revenue_year';

const RevenueTab = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(() => {
    try { const v = localStorage.getItem(LS_REVENUE_YEAR); return v ? parseInt(v) : currentYear; } catch { return currentYear; }
  });
  const [report, setReport] = useState<RevenueReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setReport(await getRevenueReport(year));
    } catch {
      setError('Failed to load revenue report.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  // persist year selection
  useEffect(() => { try { localStorage.setItem(LS_REVENUE_YEAR, String(year)); } catch {} }, [year]);

  const maxInvoiced = report
    ? Math.max(...report.monthly.map(m => m.invoiced), 1)
    : 1;

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Year selector + quick presets */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Year</label>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {/* Quick presets */}
        <div className="flex items-center gap-1.5">
          {[currentYear, currentYear - 1, currentYear - 2].map(y => (
            <button key={y} onClick={() => setYear(y)}
              className={`h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${year === y ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >{y}</button>
          ))}
        </div>
        {/* Export revenue CSV */}
        {report && (
          <button
            onClick={() => {
              const rows = [
                ['Month', '# Invoices', 'Invoiced', 'Collected', 'Outstanding'],
                ...report.monthly.map((row, i) => [
                  MONTH_NAMES[i] || row.month,
                  row.invoice_count,
                  Number(row.invoiced ?? 0).toFixed(2),
                  Number(row.collected ?? 0).toFixed(2),
                  Number(row.outstanding ?? 0).toFixed(2),
                ].join(',')),
              ];
              const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
              a.download = `revenue-${year}.csv`; a.click();
            }}
            className="ml-auto flex items-center gap-1.5 h-8 px-3 rounded-lg border border-gray-200 bg-white text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
          >
            <span className="material-symbols-outlined text-sm">download</span>
            Export CSV
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="py-16 text-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
      ) : report ? (
        <>
          {/* KPI summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total Invoiced', value: money(report.ytd.invoiced), icon: 'receipt_long', color: 'text-primary' },
              { label: 'Total Collected', value: money(report.ytd.collected), icon: 'payments', color: 'text-green-700' },
              { label: 'Outstanding', value: money(report.ytd.outstanding), icon: 'account_balance_wallet', color: 'text-amber-700' },
              { label: 'Invoice Count', value: String(report.ytd.count), icon: 'format_list_numbered', color: 'text-gray-700' },
            ].map(kpi => (
              <div key={kpi.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`material-symbols-outlined text-lg ${kpi.color}`}>{kpi.icon}</span>
                  <span className="text-xs text-gray-500 font-medium">{kpi.label}</span>
                </div>
                <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Month-by-month table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-5 py-3 font-semibold">Month</th>
                  <th className="text-right px-5 py-3 font-semibold"># Invoices</th>
                  <th className="text-right px-5 py-3 font-semibold">Invoiced</th>
                  <th className="text-right px-5 py-3 font-semibold">Collected</th>
                  <th className="text-right px-5 py-3 font-semibold">Outstanding</th>
                  <th className="px-5 py-3 w-40 hidden lg:table-cell" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {report.monthly.map((row, idx) => {
                  const monthName = MONTH_NAMES[idx] || row.month;
                  const barPct = maxInvoiced > 0 ? (row.invoiced / maxInvoiced) * 100 : 0;
                  return (
                    <tr key={row.month} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium text-gray-900">{monthName}</td>
                      <td className="px-5 py-3 text-right text-gray-600">{row.invoice_count}</td>
                      <td className="px-5 py-3 text-right font-medium text-gray-900">{money(row.invoiced)}</td>
                      <td className="px-5 py-3 text-right text-green-700 font-medium">{money(row.collected)}</td>
                      <td className={`px-5 py-3 text-right font-medium ${row.outstanding > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                        {money(row.outstanding)}
                      </td>
                      <td className="px-5 py-3 hidden lg:table-cell">
                        <div className="h-2 w-full rounded-full bg-gray-100">
                          <div
                            className="h-2 rounded-full bg-primary transition-all"
                            style={{ width: `${barPct}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};

// ── AR Aging Tab ──────────────────────────────────────────────────────────────

const ArAgingTab = () => {
  const [report, setReport] = useState<ArAgingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getArAgingReport()
      .then(setReport)
      .catch(() => setError('Failed to load AR aging report.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="py-16 text-center">
        <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    );
  }

  if (error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Bucket summary */}
      {report && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {AGING_BUCKET_KEYS.map(key => {
              const val = report.buckets[key] ?? 0;
              const isLate = key !== 'current' && val > 0;
              return (
                <div key={key} className={`rounded-xl border p-4 ${isLate ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
                  <p className="text-xs font-medium text-gray-500 mb-1">{AGING_BUCKET_LABELS[key] ?? key}</p>
                  <p className={`text-lg font-bold ${isLate ? 'text-red-700' : 'text-gray-900'}`}>
                    {money(val)}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Detail table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {report.invoices.length === 0 ? (
              <div className="p-12 text-center">
                <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">check_circle</span>
                <p className="text-sm text-gray-500">No outstanding invoices — you're all clear.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-5 py-3 font-semibold">Invoice #</th>
                      <th className="text-left px-5 py-3 font-semibold">Client</th>
                      <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Invoice Date</th>
                      <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Due Date</th>
                      <th className="text-right px-5 py-3 font-semibold">Balance</th>
                      <th className="text-right px-5 py-3 font-semibold hidden sm:table-cell">Days Overdue</th>
                      <th className="text-left px-5 py-3 font-semibold">Bucket</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {report.invoices.map(inv => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 font-semibold text-gray-900">{inv.invoice_number}</td>
                        <td className="px-5 py-3 text-gray-700">{inv.client_name}</td>
                        <td className="px-5 py-3 text-gray-500 hidden sm:table-cell whitespace-nowrap">
                          {fmtDate(inv.invoice_date)}
                        </td>
                        <td className="px-5 py-3 text-gray-500 hidden md:table-cell whitespace-nowrap">
                          {fmtDate(inv.due_date)}
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-amber-700 whitespace-nowrap">
                          {money(inv.balance_due)}
                        </td>
                        <td className={`px-5 py-3 text-right hidden sm:table-cell font-medium ${
                          inv.days_overdue > 0 ? 'text-red-700' : 'text-gray-400'
                        }`}>
                          {inv.days_overdue > 0 ? `${inv.days_overdue}d` : '—'}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            inv.aging_bucket === 'current'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {AGING_BUCKET_LABELS[inv.aging_bucket] ?? inv.aging_bucket}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ── Profitability Tab ─────────────────────────────────────────────────────────

const ProfitabilityTab = () => {
  const [rows, setRows] = useState<ProfitabilityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getProfitabilityReport()
      .then(setRows)
      .catch(() => setError('Failed to load profitability report.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="py-16 text-center">
        <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    );
  }

  if (error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {rows.length === 0 ? (
        <div className="p-12 text-center">
          <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">trending_up</span>
          <p className="text-sm text-gray-500">No profitability data available.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                <th className="text-left px-5 py-3 font-semibold">Job</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Client</th>
                <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Invoiced</th>
                <th className="text-right px-5 py-3 font-semibold hidden sm:table-cell">Collected</th>
                <th className="text-right px-5 py-3 font-semibold hidden lg:table-cell">Budget</th>
                <th className="text-right px-5 py-3 font-semibold hidden lg:table-cell">True Cost</th>
                <th className="text-right px-5 py-3 font-semibold">Burn</th>
                <th className="text-right px-5 py-3 font-semibold">Proj. Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-gray-900 truncate max-w-[180px]">{row.title}</p>
                  </td>
                  <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell truncate max-w-[140px]">
                    {row.client_name}
                  </td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium capitalize bg-gray-100 text-gray-600">
                      {row.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right font-medium text-gray-900 whitespace-nowrap">
                    {money(row.revenue_invoiced)}
                  </td>
                  <td className="px-5 py-3.5 text-right text-green-700 font-medium hidden sm:table-cell whitespace-nowrap">
                    {money(row.revenue_collected)}
                  </td>
                  <td className="px-5 py-3.5 text-right text-gray-500 hidden lg:table-cell whitespace-nowrap">
                    {money(row.budget)}
                  </td>
                  <td className="px-5 py-3.5 text-right text-gray-500 hidden lg:table-cell whitespace-nowrap"
                    title={`actual ${money(row.actual_cost)} + labour ${money(row.labour_cost ?? 0)} + expenses ${money(row.expenses_total ?? 0)}`}>
                    {money(row.true_cost ?? row.actual_cost)}
                  </td>
                  {/* Burn = true cost vs budget; amber ≥70%, red ≥90% */}
                  <td className={`px-5 py-3.5 text-right font-semibold whitespace-nowrap ${
                    (row.burn_pct ?? 0) >= 90 ? 'text-red-700' : (row.burn_pct ?? 0) >= 70 ? 'text-amber-600' : 'text-gray-600'
                  }`}>
                    {Number(row.budget) > 0 ? `${row.burn_pct ?? 0}%` : '—'}
                  </td>
                  <td className={`px-5 py-3.5 text-right font-semibold whitespace-nowrap ${
                    (row.projected_margin_pct ?? 0) < 0 ? 'text-red-700' : (row.projected_margin_pct ?? 0) < 15 ? 'text-amber-600' : 'text-green-700'
                  }`}>
                    {Number(row.budget) > 0 ? `${row.projected_margin_pct ?? 0}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Crew Utilization Tab ──────────────────────────────────────────────────────

const DAYS_OPTIONS = [30, 60, 90];
const LS_UTIL_DAYS = 'reports_util_days';

const UtilizationTab = () => {
  const [days, setDays] = useState<number>(() => {
    try { const v = localStorage.getItem(LS_UTIL_DAYS); return v ? parseInt(v) : 30; } catch { return 30; }
  });
  const [rows, setRows] = useState<UtilizationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { try { localStorage.setItem(LS_UTIL_DAYS, String(days)); } catch {} }, [days]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setRows(await getCrewUtilizationReport(days));
    } catch {
      setError('Failed to load utilization report.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  type DaysStr = '30' | '60' | '90';
  const daysStr = String(days) as DaysStr;

  return (
    <div className="space-y-6">
      {/* Days selector — SegmentedControl */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">Period</span>
        <SegmentedControl<DaysStr>
          options={DAYS_OPTIONS.map(d => ({ value: String(d) as DaysStr, label: `${d} days` }))}
          value={daysStr}
          onChange={v => setDays(Number(v))}
          size="sm"
          fullWidth={false}
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-400">
            <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">engineering</span>
            <p className="text-sm text-gray-500">No utilization data for this period.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                <th className="text-left px-5 py-3 font-semibold">Employee</th>
                <th className="text-right px-5 py-3 font-semibold">Total Hours</th>
                <th className="text-right px-5 py-3 font-semibold hidden sm:table-cell">Avg Hrs/Day</th>
                <th className="text-right px-5 py-3 font-semibold hidden md:table-cell">Entries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-gray-900">{row.name}</p>
                    <p className="text-xs text-gray-400">{row.email}</p>
                  </td>
                  <td className="px-5 py-3.5 text-right font-semibold text-gray-900">
                    {Number(row.total_hours ?? 0).toFixed(1)}h
                  </td>
                  <td className="px-5 py-3.5 text-right text-gray-600 hidden sm:table-cell">
                    {Number(row.avg_hours_per_day ?? 0).toFixed(1)}h
                  </td>
                  <td className="px-5 py-3.5 text-right text-gray-500 hidden md:table-cell">
                    {row.entry_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ── Win / Loss Tab ────────────────────────────────────────────────────────────

const WinLossTab = () => {
  const [report, setReport] = useState<WinLossReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getWinLossReport()
      .then(setReport)
      .catch(() => setError('Failed to load win/loss report.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="py-16 text-center">
        <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    );
  }
  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;
  if (!report) return null;

  const t = report.totals;
  const cards = [
    { label: 'Win Rate', value: `${t.win_rate}%`, icon: 'emoji_events', color: 'text-primary' },
    { label: 'Won', value: String(Number(t.won)), icon: 'check_circle', color: 'text-green-700' },
    { label: 'Lost', value: String(Number(t.lost)), icon: 'cancel', color: 'text-red-600' },
    { label: 'Revenue Won', value: money(Number(t.revenue_won)), icon: 'payments', color: 'text-green-700' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
              <span className={`material-symbols-outlined text-base ${c.color}`}>{c.icon}</span>{c.label}
            </div>
            <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {report.clients.length === 0 ? (
          <div className="p-12 text-center">
            <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">emoji_events</span>
            <p className="text-sm text-gray-500">No quote outcomes yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-5 py-3 font-semibold">Client</th>
                  <th className="text-right px-5 py-3 font-semibold">Won</th>
                  <th className="text-right px-5 py-3 font-semibold">Lost</th>
                  <th className="text-right px-5 py-3 font-semibold hidden sm:table-cell">Pending</th>
                  <th className="text-right px-5 py-3 font-semibold hidden sm:table-cell">Win %</th>
                  <th className="text-right px-5 py-3 font-semibold">Revenue Won</th>
                  <th className="text-right px-5 py-3 font-semibold hidden lg:table-cell">Revenue Lost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {report.clients.map((r, i) => {
                  const won = Number(r.won), lost = Number(r.lost);
                  const rate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0;
                  return (
                    <tr key={r.client_id ?? `n${i}`} className="hover:bg-gray-50">
                      <td className="px-5 py-3.5 font-medium text-gray-900 truncate max-w-[200px]">{r.client}</td>
                      <td className="px-5 py-3.5 text-right text-green-700 font-medium">{won}</td>
                      <td className="px-5 py-3.5 text-right text-red-600 font-medium">{lost}</td>
                      <td className="px-5 py-3.5 text-right text-gray-500 hidden sm:table-cell">{Number(r.pending)}</td>
                      <td className="px-5 py-3.5 text-right text-gray-700 hidden sm:table-cell">{rate}%</td>
                      <td className="px-5 py-3.5 text-right font-medium text-gray-900 whitespace-nowrap">{money(Number(r.revenue_won))}</td>
                      <td className="px-5 py-3.5 text-right text-gray-400 hidden lg:table-cell whitespace-nowrap">{money(Number(r.revenue_lost))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const LS_REPORTS_TAB = 'reports_tab';

// ── GST (CR #110) — what you owe CRA: collected on invoices minus ITCs ──────
const GstTab = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [report, setReport] = useState<GstReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getGstReport(year)
      .then(setReport)
      .catch(() => setError('Failed to load the GST report.'))
      .finally(() => setLoading(false));
  }, [year]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Year</label>
        <div className="flex items-center gap-1.5">
          {[currentYear, currentYear - 1, currentYear - 2].map(y => (
            <button key={y} onClick={() => setYear(y)}
              className={`h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${year === y ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >{y}</button>
          ))}
        </div>
        {report && (
          <button
            onClick={() => {
              const rows = [
                ['Quarter', 'Invoices', 'Invoiced (pre-tax)', 'GST collected', 'Expenses', 'GST paid (ITC)', 'Net GST owed'].join(','),
                ...report.quarters.map(q => [
                  q.quarter, q.invoice_count, q.invoiced_subtotal.toFixed(2), q.gst_collected.toFixed(2),
                  q.expense_count, q.gst_itc.toFixed(2), q.net_owed.toFixed(2),
                ].join(',')),
                ['TOTAL', '', '', report.totals.gst_collected.toFixed(2), '', report.totals.gst_itc.toFixed(2), report.totals.net_owed.toFixed(2)].join(','),
              ];
              const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
              a.download = `gst-${year}.csv`; a.click();
            }}
            className="ml-auto flex items-center gap-1.5 h-8 px-3 rounded-lg border border-gray-200 bg-white text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
          >
            <span className="material-symbols-outlined text-sm">download</span>
            Export CSV
          </button>
        )}
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      {loading ? (
        <div className="py-16 text-center text-gray-400">Loading…</div>
      ) : report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-500">GST collected on invoices</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{money(report.totals.gst_collected)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs text-gray-500">GST paid on purchases (ITC)</p>
              <p className="text-2xl font-bold text-success mt-1">{money(report.totals.gst_itc)}</p>
            </div>
            <div className="bg-primary/5 rounded-xl border border-primary/20 p-5">
              <p className="text-xs text-gray-500">Net GST owed — {report.year}</p>
              <p className={`text-2xl font-bold mt-1 ${report.totals.net_owed > 0 ? 'text-primary' : 'text-success'}`}>{money(report.totals.net_owed)}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50/60">
                    <th className="px-5 py-2.5 font-semibold">Quarter</th>
                    <th className="px-2 py-2.5 font-semibold text-right">Invoiced (pre-tax)</th>
                    <th className="px-2 py-2.5 font-semibold text-right">GST collected</th>
                    <th className="px-2 py-2.5 font-semibold text-right">GST paid (ITC)</th>
                    <th className="px-5 py-2.5 font-semibold text-right">Net owed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {report.quarters.map(q => (
                    <tr key={q.quarter} className="hover:bg-gray-50">
                      <td className="px-5 py-2.5 font-semibold text-gray-900">{q.quarter} <span className="text-[11px] font-normal text-gray-400">{q.invoice_count} inv · {q.expense_count} exp</span></td>
                      <td className="px-2 py-2.5 text-right text-gray-600">{money(q.invoiced_subtotal)}</td>
                      <td className="px-2 py-2.5 text-right font-semibold text-gray-900">{money(q.gst_collected)}</td>
                      <td className="px-2 py-2.5 text-right text-success">{money(q.gst_itc)}</td>
                      <td className={`px-5 py-2.5 text-right font-bold ${q.net_owed > 0 ? 'text-gray-900' : 'text-success'}`}>{money(q.net_owed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 text-[11px] text-gray-400 space-y-0.5">
              <p>Accrual basis (by invoice date), drafts/cancelled/revised excluded. Collection falls back to 5% of subtotal when the BC-compliance GST split wasn't used on an invoice.</p>
              <p>ITCs are estimated at 5% of expense subtotals (scanned receipt tax can include PST, which isn't recoverable). Confirm with your accountant before filing.</p>
              {report.unassigned_expenses.count > 0 && (
                <p className="text-warning">⚠ {report.unassigned_expenses.count} expense{report.unassigned_expenses.count === 1 ? '' : 's'} with tax ({money(report.unassigned_expenses.tax)}) have no date and aren't included — add dates on the job expense tabs.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const ReportsPage = () => {
  const [tab, setTab] = useState<Tab>(() => {
    try { return (localStorage.getItem(LS_REPORTS_TAB) as Tab) || 'revenue'; } catch { return 'revenue'; }
  });
  const [exportingData, setExportingData] = useState(false);
  const { toast, toasts, removeToast } = useToast();
  useEffect(() => { try { localStorage.setItem(LS_REPORTS_TAB, tab); } catch {} }, [tab]);

  const handleDataExport = async () => {
    try {
      setExportingData(true);
      const response = await apiClient.get('/export/all', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ochrepoint-export-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to generate data export.');
    } finally {
      setExportingData(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title="Reports"
          subtitle="Finance"
          actions={
            <button
              onClick={handleDataExport}
              disabled={exportingData}
              className="flex items-center gap-1.5 h-9 px-3 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-60 transition-colors"
              title="Download all your data as CSV files"
            >
              <span className="material-symbols-outlined text-base">download</span>
              <span className="hidden sm:inline">{exportingData ? 'Exporting…' : 'Export'}</span>
            </button>
          }
        >
          {/* SegmentedControl replaces button-row tab bar */}
          <SegmentedControl<Tab>
            options={TABS.map(t => ({ value: t.id, label: t.label, icon: t.icon }))}
            value={tab}
            onChange={setTab}
            size="sm"
          />
        </LargeTitleHeader>

        {/* Tab content */}
        {tab === 'revenue' && <RevenueTab />}
        {tab === 'ar-aging' && <ArAgingTab />}
        {tab === 'profitability' && <ProfitabilityTab />}
        {tab === 'utilization' && <UtilizationTab />}
        {tab === 'win-loss' && <WinLossTab />}
        {tab === 'gst' && <GstTab />}
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </MainLayout>
  );
};
